import type { ReactNode } from 'react';
import loadable from '@loadable/component';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { Button } from '@simplywallst/ui-core';
import { useIndustryContext } from './components/IndustryContextProvider';
import ReportSubSection from '@components/ReportSubSection';
import MultipleDropdown from '../MultipleDropdown';
import { DropdownWrapper, IndustryChartFallback } from './styled';
import type { PreferredMultiple } from '../../selector';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import {
  ValuationTranslation,
  useValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { useIndustrySource } from '../../hooks/useIndustrySource';
import { useMetricFullLabel } from '../../hooks/useLabel';
import { SWS_HOST } from '@/constants';

const MultipleVsIndustryChartContainer = loadable(
  () =>
    import(
      './components/MultipleVsIndustryChart/MultipleVsIndustryChartContainer'
    ),
  {
    ssr: false,
    fallback: <IndustryChartFallback />,
  }
);

const MultipleVsIndustryTable = loadable(
  () => import('./components/MultipleVsIndustryTable')
);

const HelpContent = () => {
  return (
    <div>
      <ValuationTranslation i18nKey="vsIndustry.help.description">
        <p>
          Comparing a company to its industry is useful to gain a wider
          perspective of how it is valued. The advantage is that you generally
          have more companies to compare to, with the trade off being that you
          are comparing with companies that may be at a different stage of the
          lifecycle to the company you are valuing.
        </p>
        <p>
          For a more detailed breakdown of how compare stocks to their industry,
          please check out our
          <Button
            as="a"
            target="_blank"
            href={`${SWS_HOST}/help/articles/hc/en-us/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report?section=Comparison-to-Industry`}
            rel="noopener noreferrer"
            styleType="strongLink"
          >
            Help Centre.
          </Button>
        </p>
      </ValuationTranslation>
    </div>
  );
};

interface MultipleVsIndustryProps {
  preferredMultiple: PreferredMultiple;
  statements?: ReactNode;
}

const MultipleVsIndustry = ({
  preferredMultiple,
  statements,
}: MultipleVsIndustryProps) => {
  const { multiple, setMultiple } = useIndustryContext();
  //Get info about selected company from API
  const { multiples, tickerSymbol, uniqueSymbol, canonicalUrl } =
    useValuationData();
  const isPristine = useVisibleSectionsIsPristine();

  const resolvedMultiple = multiple || preferredMultiple;

  const { industryName, industrySource } = useIndustrySource();

  const t = useValuationTranslation();
  const getMetricFullLabel = useMetricFullLabel();

  return (
    <>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title={t('vsIndustry.heading', {
          metricLabel: getMetricFullLabel(resolvedMultiple),
        })}
        description={
          industrySource !== undefined
            ? t('vsIndustry.description.withSource', {
                tickerSymbol,
                metric: resolvedMultiple,
                industrySource,
                industryName,
              })
            : t('vsIndustry.description.withoutSource', {
                tickerSymbol,
                metric: resolvedMultiple,
                industryName,
              })
        }
        bodyRender={() => (
          <>
            <DropdownWrapper>
              <MultipleDropdown
                id="vsIndustry"
                multiple={resolvedMultiple}
                preferredMultiple={preferredMultiple}
                onSelect={setMultiple}
              />
            </DropdownWrapper>
            <ChartWrapper
              id="valuation-multiple-vs-industry"
              chart={
                <>
                  <MultipleVsIndustryChartContainer
                    multiples={multiples}
                    tickerSymbol={tickerSymbol}
                    uniqueSymbol={uniqueSymbol}
                    canonicalUrl={canonicalUrl}
                  />
                  <MultipleVsIndustryTable />
                </>
              }
              table={
                <>
                  <IndustryChartFallback />
                  <MultipleVsIndustryTable />
                </>
              }
              feedbackLabel={t('vsIndustry.feedback')}
              help={<HelpContent />}
              helpTitle={t('vsIndustry.help.title')}
              preRenderHeight={614}
              preRenderHeightSmDown={628}
              hideDataButton
            />
          </>
        )}
        statements={statements}
      />
    </>
  );
};
export default MultipleVsIndustry;
